"use client";

import { useState, type ReactNode } from "react";
import { CopyIcon } from "@/app/docs/docs-icons";

/* ------------------------------------------------ minimal highlighters */

function jsonTokens(src: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // key ("...":), string value, number/bool/null, punctuation
  const re =
    /("(?:[^"\\]|\\.)*")(\s*:)|("(?:[^"\\]|\\.)*")|(\b-?\d+(?:\.\d+)?\b|\btrue\b|\bfalse\b|\bnull\b)|([{}\[\],])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(src))) {
    if (m.index > last) nodes.push(src.slice(last, m.index));
    if (m[1] !== undefined) {
      nodes.push(
        <span key={i++} className="text-[#7dd3fc]">
          {m[1]}
        </span>,
      );
      nodes.push(
        <span key={i++} className="text-white/40">
          {m[2]}
        </span>,
      );
    } else if (m[3] !== undefined) {
      nodes.push(
        <span key={i++} className="text-[#86efac]">
          {m[3]}
        </span>,
      );
    } else if (m[4] !== undefined) {
      nodes.push(
        <span key={i++} className="text-[#fcd34d]">
          {m[4]}
        </span>,
      );
    } else if (m[5] !== undefined) {
      nodes.push(
        <span key={i++} className="text-white/40">
          {m[5]}
        </span>,
      );
    }
    last = re.lastIndex;
  }
  if (last < src.length) nodes.push(src.slice(last));
  return nodes;
}

function httpTokens(src: string): ReactNode[] {
  return src.split("\n").map((line, idx) => {
    let content: ReactNode = line;
    const methodMatch = line.match(/^(GET|POST|PUT|PATCH|DELETE)\s+(.*)$/);
    const headerMatch = line.match(/^([A-Za-z-]+):\s?(.*)$/);
    if (methodMatch) {
      content = (
        <>
          <span className="font-semibold text-brand-cyan">{methodMatch[1]}</span>{" "}
          <span className="text-[#7dd3fc]">{methodMatch[2]}</span>
        </>
      );
    } else if (headerMatch) {
      content = (
        <>
          <span className="text-[#c4b5fd]">{headerMatch[1]}</span>
          <span className="text-white/40">: </span>
          <span className="text-white/80">{headerMatch[2]}</span>
        </>
      );
    }
    return (
      <span key={idx} className="block">
        {content || " "}
      </span>
    );
  });
}

/* --------------------------------------------------------- component */

type CodeBlockProps = {
  code: string;
  language?: "json" | "http" | "bash" | "text";
  label?: string;
};

export function CodeBlock({ code, language = "text", label }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const trimmed = code.replace(/\n$/, "");

  async function copy() {
    try {
      await navigator.clipboard.writeText(trimmed);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  }

  let highlighted: ReactNode;
  if (language === "json") highlighted = jsonTokens(trimmed);
  else if (language === "http") highlighted = httpTokens(trimmed);
  else highlighted = trimmed;

  return (
    <div className="my-5 overflow-hidden rounded-lg border border-white/10 bg-[#0b1220]">
      <div className="flex items-center justify-between border-b border-white/10 px-3.5 py-2">
        <span className="font-mono text-[11px] font-medium uppercase tracking-wider text-white/40">
          {label ?? language}
        </span>
        <button
          type="button"
          onClick={copy}
          aria-label={copied ? "Code copied" : "Copy code to clipboard"}
          className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-medium text-white/60 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-cyan"
        >
          <CopyIcon className="h-3.5 w-3.5" />
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-3.5 text-[13px] leading-[1.7]">
        <code className="font-mono text-white/90">{highlighted}</code>
      </pre>
    </div>
  );
}
